import React from "react";

/**
 * Lightweight repo-native SVG diagrams for the SORA 2.5 Step 2 "maximum characteristic dimension"
 * (CAA CZ: "maximální charakteristický rozměr"), one per airframe construction:
 *  - fixed-wing: wingspan tip-to-tip — CAA CZ's guidance ("rozpětí křídel") applies this to fixed-wing
 *    UA generally, "regardless of wing count or hybrid configurations", so a fixed-wing VTOL/hybrid also
 *    measures wingspan, NOT multirotor blade-tip distance.
 *  - helicopter: main rotor diameter
 *  - multirotor: farthest distance between rotor/blade tips at worst-case rotor position
 * Definitions per CAA CZ SORA 2.5 Step 2 guidance (EASA-sourced) — not copied images, drawn locally to
 * avoid hotlinking/licensing an external site's assets. Pure line art via currentColor so it follows the
 * surrounding text color in both themes.
 *
 * "vtol" (DroneCatalogEntry.DroneType) is deliberately NOT auto-mapped to any single diagram here — a
 * VTOL/hybrid airframe can be built around either fixed-wing (wingspan) or multirotor (blade-tip) geometry,
 * and the catalog's bare "vtol" value alone doesn't say which. See diagramForDroneType/
 * isAmbiguousCatalogDroneType below.
 */

const DiagramFrame = ({ children, label }) => (
  <svg viewBox="0 0 120 90" className="w-full h-auto" role="img" aria-label={label}>
    {children}
  </svg>
);

const ArrowMarkers = () => (
  <defs>
    <marker id="cd-arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
      <path d="M0,0 L10,5 L0,10 z" fill="currentColor" />
    </marker>
  </defs>
);

export const FixedWingDiagram = ({ label }) => (
  <DiagramFrame label={label}>
    <ArrowMarkers />
    {/* fuselage (top-down) */}
    <ellipse cx="60" cy="52" rx="7" ry="22" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.85" />
    {/* wings */}
    <path d="M60,44 L8,58 L18,62 L60,52 Z" fill="currentColor" opacity="0.35" stroke="currentColor" strokeWidth="1" />
    <path d="M60,44 L112,58 L102,62 L60,52 Z" fill="currentColor" opacity="0.35" stroke="currentColor" strokeWidth="1" />
    {/* tail */}
    <path d="M60,72 L48,78 L60,74 L72,78 Z" fill="currentColor" opacity="0.5" />
    {/* wingspan dimension line */}
    <line x1="8" y1="24" x2="112" y2="24" stroke="currentColor" strokeWidth="1" markerStart="url(#cd-arrow)" markerEnd="url(#cd-arrow)" />
    <line x1="8" y1="20" x2="8" y2="58" stroke="currentColor" strokeWidth="0.75" strokeDasharray="2,2" opacity="0.6" />
    <line x1="112" y1="20" x2="112" y2="58" stroke="currentColor" strokeWidth="0.75" strokeDasharray="2,2" opacity="0.6" />
  </DiagramFrame>
);

export const HelicopterDiagram = ({ label }) => (
  <DiagramFrame label={label}>
    <ArrowMarkers />
    {/* rotor disc */}
    <circle cx="60" cy="48" r="34" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
    {/* secondary blade — faint structural reference only, carries no dimension marker so the diagram
        can't be misread as measuring a distance between two different rotors/blades */}
    <line x1="27" y1="66" x2="93" y2="30" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
    {/* rotor-diameter dimension: drawn directly along this blade's own axis — both arrowheads sit on
        this one blade's own two tips (opposite ends of the same straight line through the hub), not on
        two different blades' near-side tips like the previous offset-horizontal-line version did */}
    <line x1="27" y1="30" x2="93" y2="66" stroke="currentColor" strokeWidth="1.75" markerStart="url(#cd-arrow)" markerEnd="url(#cd-arrow)" />
    {/* hub + fuselage — kept short and below the hub so it never crosses either blade line */}
    <circle cx="60" cy="48" r="4" fill="currentColor" />
    <path d="M55,60 L50,80 L70,80 L65,60 Z" fill="currentColor" opacity="0.3" stroke="currentColor" strokeWidth="1" />
  </DiagramFrame>
);

export const MultirotorDiagram = ({ label }) => {
  const arms = [
    [60, 48, 20, 20],
    [60, 48, 100, 20],
    [60, 48, 20, 76],
    [60, 48, 100, 76],
  ];
  return (
    <DiagramFrame label={label}>
      <ArrowMarkers />
      {/* frame arms — faded so the bold dimension arrow below reads as clearly foreground, not blended
          into a same-color, similar-angle structural line underneath it */}
      {arms.map(([x1, y1, x2, y2], i) => (
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
      ))}
      {/* body */}
      <rect x="53" y="41" width="14" height="14" rx="3" fill="currentColor" opacity="0.4" stroke="currentColor" strokeWidth="1" />
      {/* rotor discs (worst-case: blade tips extend past the arm end) */}
      {[[20, 20], [100, 20], [20, 76], [100, 76]].map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r="13" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="2,2" opacity="0.6" />
      ))}
      {/* farthest blade-tip-to-blade-tip dimension: same worst-case diagonal pair as before, but bowed
          (quadratic curve, not a straight line) so it visibly routes around the central body block
          instead of running straight through it, and drawn bolder/fully opaque so it reads as clearly
          separate from the (now faded) frame arm running almost the same path underneath it — a plain
          straight, thin line here was nearly indistinguishable from the arm directly beneath it. */}
      <path
        d="M9,13 Q76,25 111,83"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        markerStart="url(#cd-arrow)"
        markerEnd="url(#cd-arrow)"
      />
    </DiagramFrame>
  );
};

/** droneType: multirotor | fixed-wing | vtol | helicopter | other (DroneCatalogEntry.DroneType wire values) */
export const DIAGRAM_KEYS = ["fixed_wing", "helicopter", "multirotor"];

/**
 * Maps an unambiguous catalog drone_type to its diagram. Returns null for "vtol" (ambiguous — could be
 * fixed-wing-hybrid or multirotor-based, see the file-level comment above), "other", or anything
 * unrecognized/missing — all of which fall back to CharacteristicDimensionField's manual selector rather
 * than guessing.
 */
export const diagramForDroneType = (droneType) => {
  switch (droneType) {
    case "fixed-wing": return "fixed_wing";
    case "helicopter": return "helicopter";
    case "multirotor": return "multirotor";
    default: return null;
  }
};

/** True for a catalog drone_type that is a recognized value but still doesn't determine a single
 * diagram (currently just "vtol") — used to show a more specific explanation than the generic
 * "no catalog data" manual-selector prompt. */
export const isAmbiguousCatalogDroneType = (droneType) => droneType === "vtol";

export const CharacteristicDimensionDiagram = ({ diagramKey, label }) => {
  if (diagramKey === "fixed_wing") return <FixedWingDiagram label={label} />;
  if (diagramKey === "helicopter") return <HelicopterDiagram label={label} />;
  if (diagramKey === "multirotor") return <MultirotorDiagram label={label} />;
  return null;
};
